import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import './FlagAnimation.css';

function FlagAnimation({ width = 320, height = 200 }) {
  const filterRef = useRef(null);
  const flagRef = useRef(null);
  const chakraRef = useRef(null);

  useEffect(() => {
    if (filterRef.current) {
      // Very smooth, subtle turbulence baseFrequency animation for soft waves
      const tl = gsap.timeline({ repeat: -1 });
      
      tl.to(filterRef.current, {
        attr: { baseFrequency: '0.015 0.04' },
        duration: 3.5,
        ease: 'sine.inOut'
      })
      .to(filterRef.current, {
        attr: { baseFrequency: '0.01 0.025' },
        duration: 3.5,
        ease: 'sine.inOut'
      });

      // Small gentle physics swing for the whole flag
      gsap.to(flagRef.current, {
        rotateZ: 0.8,
        yPercent: 0.5,
        duration: 2.5,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut'
      });

      // Gentle continuous spin for the Navy blue Chakra
      gsap.to(chakraRef.current, {
        rotation: 360,
        transformOrigin: '50% 50%',
        duration: 25,
        repeat: -1,
        ease: 'none'
      });
    }
  }, []);

  const spokes = Array.from({ length: 24 });

  return (
    <div className="flag-container">
      {/* Premium Metallic Flagpole */}
      <div className="flag-pole">
        <div className="flag-pole-cap" />
      </div>

      {/* Flag SVG */}
      <svg
        className="flag-svg"
        viewBox="0 0 300 200"
        width={width}
        height={height}
        ref={flagRef}
      >
        <defs>
          {/* Subtle displacement map for realistic satin-like cloth ripple */}
          <filter id="wind-wave" x="-10%" y="-10%" width="120%" height="120%">
            <feTurbulence
              ref={filterRef}
              type="fractalNoise"
              baseFrequency="0.01 0.03"
              numOctaves="3"
              result="noise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale="10" /* Reduced scale from 22 to 10 for clean shape and details */
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>

        <g filter="url(#wind-wave)">
          {/* Saffron Stripe */}
          <rect x="0" y="0" width="300" height="66.6" fill="#ff9933" />
          
          {/* White Stripe */}
          <rect x="0" y="66.6" width="300" height="66.6" fill="#ffffff" />
          
          {/* Green Stripe */}
          <rect x="0" y="133.2" width="300" height="66.8" fill="#138808" />
          
          {/* Ashoka Chakra - Inline SVG elements to center perfectly and scale cleanly */}
          <g transform="translate(150, 100)" ref={chakraRef}>
            <circle cx="0" cy="0" r="23" fill="none" stroke="#000080" strokeWidth="2.2" />
            <circle cx="0" cy="0" r="3.5" fill="#000080" />
            {spokes.map((_, i) => {
              const angle = (i * 15 * Math.PI) / 180;
              const x2 = 22 * Math.cos(angle);
              const y2 = 22 * Math.sin(angle);
              return (
                <line
                  key={i}
                  x1="0"
                  y1="0"
                  x2={x2}
                  y2={y2}
                  stroke="#000080"
                  strokeWidth="0.9"
                />
              );
            })}
          </g>
        </g>
      </svg>
      <div className="flag-shadow" />
    </div>
  );
}

export default FlagAnimation;
